'use client'

import type React from 'react'

import { StatsigProvider as _StatsigProvider } from '@statsig/react-bindings'
import { useEffect } from 'react'

import { MomentEnv } from '@/app/moment-env'
import { LoadingIcon } from '@/components/atomic/loading-icon'
import { createLogger } from '@/lib/logger'

const LOGGER = createLogger('AnonymousStatsigProvider')

// Anonymous Statsig user object (constant to avoid recreating on each render)
const ANONYMOUS_STATSIG_USER = {
  userID: 'anonymous',
}

// Loading component to show while Statsig initializes (constant to avoid recreating on each render)
const LOADING_COMPONENT = (
  <div className="flex h-screen w-full items-center justify-center">
    <LoadingIcon />
  </div>
)

// Event logging is routed through a same-origin proxy (/api/statsig) to
// bypass ad blockers that block prodregistryv2.org / beyondwickedmapping.org
// (net::ERR_BLOCKED_BY_CLIENT). The proxy forwards to events.statsigapi.net.
const STATSIG_OPTIONS = {
  networkConfig: {
    logEventUrl: '/api/statsig',
  },
  environment: {
    tier: MomentEnv.StatsigEnvironment,
  },
}

/**
 * StatsigProvider for anonymous/unauthenticated users (e.g., login page)
 *
 * This provider initializes Statsig without requiring user authentication,
 * allowing feature flags to work on public pages like login.
 */

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function AnonymousStatsigProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { StatsigDisabled } = MomentEnv

  useEffect(() => {
    // StatsigDisabled is a build-time constant — empty dep array runs once.
    if (StatsigDisabled) {
      LOGGER.info(
        'AnonymousStatsigProvider - Statsig is disabled. Feature flags use local overrides and fall back to defaults.',
      )
    }
  }, [])

  // Build-time kill-switch: when STATSIG_DISABLED=true was set at build,
  // skip mounting the SDK entirely. AppConfigProvider's noop source takes over.
  if (StatsigDisabled) {
    return <>{children}</>
  }

  // Enabled branch: missing key here is a misconfiguration — fail loud.
  const sdkKey = MomentEnv.StatsigClientKey
  if (!sdkKey) {
    throw new Error(
      'Statsig is enabled for this build but NEXT_PUBLIC_STATSIG_CLIENT_KEY is empty. ' +
        'Set the key in the environment or set STATSIG_DISABLED=true at build time.',
    )
  }

  return (
    <_StatsigProvider
      sdkKey={sdkKey}
      user={ANONYMOUS_STATSIG_USER}
      loadingComponent={LOADING_COMPONENT}
      options={STATSIG_OPTIONS}
    >
      {children}
    </_StatsigProvider>
  )
}
